import React, { useState } from "react";
import AdminTable from "../../Common/AdminTable";
import AdminModal from "../../Common/AdminModal";
import { ADMIN_ROLES } from "../../../../constants/adminPermissions";
import { recordAuditLog } from "../../../../services/adminService";
import { useAdminAuth } from "../../../../contexts/AdminAuthContext";
import { Plus, Shield, CheckCircle2 } from "lucide-react";

const INITIAL_ADMINS = [
  {
    id: "adm_1",
    name: "Sekhar (Super Admin)",
    email: "admin@edukemy.com",
    role: ADMIN_ROLES.SUPER_ADMIN,
    status: "Active",
    last_active: "Just now",
  },
  {
    id: "adm_2",
    name: "Dr. R. Sharma (Academics Lead)",
    email: "academics@edukemy.com",
    role: ADMIN_ROLES.ACADEMICS,
    status: "Active",
    last_active: "2 hours ago",
  },
  {
    id: "adm_3",
    name: "Pooja Nair (Operations)",
    email: "operations@edukemy.com",
    role: ADMIN_ROLES.OPERATIONS,
    status: "Active",
    last_active: "Yesterday",
  },
  {
    id: "adm_4",
    name: "Karan Johar (Support)",
    email: "support@edukemy.com",
    role: ADMIN_ROLES.SUPPORT,
    status: "Active",
    last_active: "3 days ago",
  },
];

export default function AdminUsersPage() {
  const { adminUser } = useAdminAuth();
  const [adminUsers, setAdminUsers] = useState(INITIAL_ADMINS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [newAdminForm, setNewAdminForm] = useState({
    name: "",
    email: "",
    role: ADMIN_ROLES.ACADEMICS,
  });

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 3500);
  };

  const handleRoleChange = (adminId, newRole) => {
    const target = adminUsers.find((a) => a.id === adminId);
    if (!target) return;
    const originalRole = target.role;

    setAdminUsers(
      adminUsers.map((a) => (a.id === adminId ? { ...a, role: newRole } : a))
    );

    recordAuditLog({
      action: "Admin Role Changed",
      module: "Admin",
      entityType: "AdminUser",
      entityId: adminId,
      beforeValue: { role: originalRole },
      afterValue: { role: newRole },
      adminUser: adminUser.email,
    });

    showToast(`Role for ${target.name} changed to ${newRole}`);
  };

  const handleToggleStatus = (adminId) => {
    const target = adminUsers.find((a) => a.id === adminId);
    if (!target) return;
    const nextStatus = target.status === "Active" ? "Inactive" : "Active";

    setAdminUsers(
      adminUsers.map((a) => (a.id === adminId ? { ...a, status: nextStatus } : a))
    );

    recordAuditLog({
      action: "Admin Status Changed",
      module: "Admin",
      entityType: "AdminUser",
      entityId: adminId,
      beforeValue: { status: target.status },
      afterValue: { status: nextStatus },
      adminUser: adminUser.email,
    });

    showToast(`Admin ${target.name} is now ${nextStatus}`);
  };

  const handleAddAdmin = () => {
    if (!newAdminForm.name.trim() || !newAdminForm.email.trim()) {
      alert("Name and email are required");
      return;
    }

    const created = {
      id: `adm_${Date.now()}`,
      name: newAdminForm.name,
      email: newAdminForm.email,
      role: newAdminForm.role,
      status: "Active",
      last_active: "Never",
    };

    setAdminUsers([...adminUsers, created]);
    setIsModalOpen(false);

    recordAuditLog({
      action: "Admin User Added",
      module: "Admin",
      entityType: "AdminUser",
      entityId: created.id,
      beforeValue: null,
      afterValue: created,
      adminUser: adminUser.email,
    });

    showToast(`Admin ${created.name} added successfully!`);
    setNewAdminForm({ name: "", email: "", role: ADMIN_ROLES.ACADEMICS });
  };

  const columns = [
    {
      title: "Admin Name",
      key: "name",
      render: (val, row) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{val}</div>
          <div style={{ fontSize: "12px", color: "#64748b" }}>{row.email}</div>
        </div>
      ),
    },
    {
      title: "Assigned Role",
      key: "role",
      width: "200px",
      render: (val, row) => (
        <select
          className="admin-form-select"
          style={{ padding: "4px 8px", fontSize: "12.5px", fontWeight: 600 }}
          value={val}
          onChange={(e) => handleRoleChange(row.id, e.target.value)}
        >
          {Object.values(ADMIN_ROLES).map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "110px",
      render: (val) => {
        const cls = (val || "").toLowerCase();
        return <span className={`status-badge ${cls}`}>{val}</span>;
      },
    },
    {
      title: "Last Active",
      key: "last_active",
      width: "140px",
      render: (val) => (
        <span style={{ fontSize: "12.5px", color: "#64748b" }}>{val}</span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "130px",
      render: (_, row) => (
        <button
          className={`admin-btn admin-btn-sm ${row.status === "Active" ? "admin-btn-secondary" : "admin-btn-primary"}`}
          onClick={() => handleToggleStatus(row.id)}
        >
          {row.status === "Active" ? "Deactivate" : "Activate"}
        </button>
      ),
    },
  ];

  return (
    <div>
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            background: "#0f172a",
            color: "#ffffff",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 2000,
            fontSize: "14px",
          }}
        >
          <CheckCircle2 size={18} style={{ color: "#22c55e" }} />
          {toastMessage}
        </div>
      )}

      <AdminTable
        columns={columns}
        data={adminUsers}
        searchPlaceholder="Search admin staff by name, email..."
        actions={
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => setIsModalOpen(true)}
          >
            <Plus size={15} /> Add Admin User
          </button>
        }
        pageSize={10}
        rowKey="id"
      />

      <AdminModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Add New Admin User"
        maxWidth="500px"
        footer={
          <>
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="admin-btn admin-btn-primary"
              onClick={handleAddAdmin}
            >
              Add Admin
            </button>
          </>
        }
      >
        <div className="admin-form-group">
          <label className="admin-form-label">Full Name *</label>
          <input
            type="text"
            className="admin-form-input"
            placeholder="e.g. Ramesh Kumar"
            value={newAdminForm.name}
            onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
          />
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Email Address *</label>
          <input
            type="email"
            className="admin-form-input"
            placeholder="e.g. ramesh@edukemy.com"
            value={newAdminForm.email}
            onChange={(e) => setNewAdminForm({ ...newAdminForm, email: e.target.value })}
          />
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Role Assignment *</label>
          <select
            className="admin-form-select"
            value={newAdminForm.role}
            onChange={(e) => setNewAdminForm({ ...newAdminForm, role: e.target.value })}
          >
            {Object.values(ADMIN_ROLES).map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
      </AdminModal>
    </div>
  );
}
